import React, { useRef, useState } from "react";
import { Button, Dropdown, Form, ProgressBar } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import PageTitle from "../../../layouts/PageTitle";
import { toast } from "react-toastify";
import {
  uploadBulkExcel,
  uploadPlayerBulkData,
  uploadCoachBulkData,
  uploadStaffBulkData,
} from "../../../../services/TenantsService";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const BulkDataUpload = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Tenant comes from previous page
  const tenant = location.state?.tenant || null;

  const fileInputRef = useRef(null);

  const [dataType, setDataType] = useState("player");
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null); // stores API response after upload

  // Tenant ID from state
  const tenantId = tenant?.id;

  const allowedFileTypes = [".csv", ".xlsx", ".xls"];

  // --------------------------------------------------
  // DATA TYPE LABEL
  // --------------------------------------------------
  const getDataTypeLabel = (type) => {
    switch (type) {
      case "coach":
        return "Coach";

      case "player":
        return "Player";

      case "staff":
        return "Staff";

      default:
        return "";
    }
  };

  // --------------------------------------------------
  // FILE SELECT
  // --------------------------------------------------
  const handleFileSelect = (file) => {
    if (!file) return;

    const fileExtension = "." + file.name.split(".").pop().toLowerCase();

    // Validate file type
    if (!allowedFileTypes.includes(fileExtension)) {
      toast.error("Please upload a CSV or Excel file.", {
        position: "top-right",
        autoClose: 1500,
      });

      return;
    }

    // Validate file size - 10MB
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size should not exceed 10 MB.", {
        position: "top-right",
        autoClose: 1500,
      });

      return;
    }

    setSelectedFile(file);
    setUploadProgress(0);
    setUploadResult(null); // clear previous result on new file
  };

  // --------------------------------------------------
  // INPUT FILE
  // --------------------------------------------------
  const handleInputChange = (event) => {
    const file = event.target.files?.[0];

    handleFileSelect(file);
  };

  // --------------------------------------------------
  // DRAG & DROP
  // --------------------------------------------------
  const handleDrop = (event) => {
    event.preventDefault();

    setIsDragging(false);

    const file = event.dataTransfer.files?.[0];

    handleFileSelect(file);
  };

  const handleDragOver = (event) => {
    event.preventDefault();

    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  // --------------------------------------------------
  // REMOVE FILE
  // --------------------------------------------------
  const removeFile = () => {
    setSelectedFile(null);
    setUploadProgress(0);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // --------------------------------------------------
  // UPLOAD
  // Step 1: Upload excel file → get file metadata
  // Step 2: Pass metadata as payload to type-specific API
  // --------------------------------------------------
  const handleUpload = async () => {
    if (!tenantId) {
      toast.error("Tenant ID is missing.", {
        position: "top-right",
        autoClose: 1500,
      });
      return;
    }

    if (!selectedFile) {
      toast.warning("Please select a file first.", {
        position: "top-right",
        autoClose: 1500,
      });
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(10);
      setUploadResult(null);

      // --------------------------------------------------
      // STEP 1: Upload file to get metadata
      // Returns: { fileName, bucketName, fileUrl, contentType, size }
      // --------------------------------------------------
      const fileMetadata = await uploadBulkExcel(selectedFile);
      setUploadProgress(50);

      // --------------------------------------------------
      // STEP 2: Build payload as single object
      // --------------------------------------------------
      const payload = {
        fileName: fileMetadata?.fileName,
        bucketName: fileMetadata?.bucketName,
        fileUrl: fileMetadata?.fileUrl,
        contentType: fileMetadata?.contentType,
        size: fileMetadata?.size,
      };

      // --------------------------------------------------
      // STEP 3: Call type-specific bulk upload API
      // --------------------------------------------------
      let result = null;
      if (dataType === "player") {
        result = await uploadPlayerBulkData(tenantId, payload);
      } else if (dataType === "coach") {
        result = await uploadCoachBulkData(tenantId, payload);
      } else if (dataType === "staff") {
        result = await uploadStaffBulkData(tenantId, payload);
      }

      setUploadProgress(100);
      setUploadResult(result);

      if (result?.success === false) {
        toast.warning(result?.message || "Upload completed with errors.", {
          position: "top-right",
          autoClose: 2000,
        });
      } else {
        toast.success(
          `${getDataTypeLabel(dataType)} data uploaded successfully!`,
          { position: "top-right", autoClose: 1500 },
        );
      }

      // Reset file
      setSelectedFile(null);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Bulk upload failed:", error);
      // Try to extract server error response
      const errData = error?.response?.data;
      if (errData) {
        setUploadResult(errData);
      }
      toast.error(
        errData?.message || "Bulk upload failed. Please try again.",
        { position: "top-right", autoClose: 2000 },
      );
    } finally {
      setIsUploading(false);
    }
  };

  // --------------------------------------------------
  // TEMPLATE HEADERS
  // --------------------------------------------------
  const getTemplateHeaders = () => {
    if (dataType === "player")
      return ["Name", "Email", "Phone", "DOB", "Address", "Parent Name", "Gender"];
    if (dataType === "coach")
      return ["Name", "Email", "Contact Phone", "DOB", "Gender", "Address", "Experience", "Skills"];
    if (dataType === "staff")
      return ["Name", "Email", "Phone Number", "Gender", "DOB", "Address"];
    return [];
  };


  const downloadExcelTemplate = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = "4lines Academy";
      workbook.created = new Date();

      const sheetName = `${getDataTypeLabel(dataType)} Template`;
      const worksheet = workbook.addWorksheet(sheetName, {
        views: [{ state: "frozen", ySplit: 1 }],
      });

      let columns = [];
      let sampleData = [];

      if (dataType === "player") {
        columns = [
          { header: "Name", key: "Name", width: 22 },
          { header: "Email", key: "Email", width: 26 },
          { header: "Phone", key: "Phone", width: 16 },
          { header: "DOB", key: "DOB", width: 16 },
          { header: "Address", key: "Address", width: 28 },
          { header: "Parent Name", key: "Parent Name", width: 22 },
          { header: "Gender", key: "Gender", width: 16 },
        ];
        sampleData = [
          {
            Name: "Rahul Sharma",
            Email: "rahul.sharma@example.com",
            Phone: "9876543210",
            DOB: "10-05-2005",
            Address: "123 Main Street, City",
            "Parent Name": "Rajesh Sharma",
            Gender: "Male",
          },
        ];
      } else if (dataType === "coach") {
        columns = [
          { header: "Name", key: "Name", width: 22 },
          { header: "Email", key: "Email", width: 26 },
          { header: "Contact Phone", key: "Contact Phone", width: 18 },
          { header: "DOB", key: "DOB", width: 16 },
          { header: "Gender", key: "Gender", width: 16 },
          { header: "Address", key: "Address", width: 28 },
          { header: "Experience", key: "Experience", width: 16 },
          { header: "Skills", key: "Skills", width: 18 },
        ];
        sampleData = [
          {
            Name: "Amit Kumar",
            Email: "amit.kumar@example.com",
            "Contact Phone": "9876543210",
            DOB: "15-08-1988",
            Gender: "Male",
            Address: "456 Park Avenue, City",
            Experience: "5 Years",
            Skills: "Batting",
          },
        ];
      } else if (dataType === "staff") {
        columns = [
          { header: "Name", key: "Name", width: 22 },
          { header: "Email", key: "Email", width: 26 },
          { header: "Phone Number", key: "Phone Number", width: 18 },
          { header: "Gender", key: "Gender", width: 16 },
          { header: "DOB", key: "DOB", width: 16 },
          { header: "Address", key: "Address", width: 28 },
        ];
        sampleData = [
          {
            Name: "Priya Singh",
            Email: "priya.singh@example.com",
            "Phone Number": "9876543210",
            Gender: "Female",
            DOB: "20-11-1995",
            Address: "789 Lake View, City",
          },
        ];
      }

      worksheet.columns = columns;

      // 1. Style Header Row
      const headerRow = worksheet.getRow(1);
      headerRow.height = 26;
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 11 };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF2563EB" }, // Blue
        };
        cell.alignment = { vertical: "middle", horizontal: "center" };
        cell.border = {
          top: { style: "thin", color: { argb: "FFCBD5E1" } },
          left: { style: "thin", color: { argb: "FFCBD5E1" } },
          bottom: { style: "medium", color: { argb: "FF1E40AF" } },
          right: { style: "thin", color: { argb: "FFCBD5E1" } },
        };
      });

      // 2. Add Sample Data Row
      sampleData.forEach((item) => {
        const row = worksheet.addRow(item);
        row.height = 22;
        row.eachCell((cell) => {
          cell.alignment = { vertical: "middle" };
          cell.font = { size: 10, color: { argb: "FF334155" } };
        });
      });

      // 3. Find column indices for Gender, Skills, and Date fields (DOB, etc.)
      let genderColIndex = null;
      let skillsColIndex = null;
      const dateColIndices = [];

      columns.forEach((col, idx) => {
        const colNum = idx + 1;
        const h = col.header.toLowerCase();
        if (h === "gender") {
          genderColIndex = colNum;
        }
        if (h === "skills") {
          skillsColIndex = colNum;
        }
        if (h === "dob" || h.includes("date") || h === "doe") {
          dateColIndices.push(colNum);
        }
      });

      // 4. Apply Data Validation (Gender & Skills dropdowns) and Text formatting for Date fields across rows 2 to 1000
      for (let r = 2; r <= 1000; r++) {
        const row = worksheet.getRow(r);

        // Gender Dropdown (Male, Female, Others)
        if (genderColIndex) {
          const genderCell = row.getCell(genderColIndex);
          genderCell.dataValidation = {
            type: "list",
            allowBlank: true,
            formulae: ['"Male,Female,Others"'],
            showErrorMessage: true,
            errorTitle: "Invalid Gender",
            error: "Please select 'Male', 'Female', or 'Others' from the dropdown list.",
          };
          if (r > 2) {
            genderCell.alignment = { vertical: "middle", horizontal: "center" };
          }
        }

        // Skills Dropdown (Batting, Bowling, Both)
        if (skillsColIndex) {
          const skillsCell = row.getCell(skillsColIndex);
          skillsCell.dataValidation = {
            type: "list",
            allowBlank: true,
            formulae: ['"Batting,Bowling,Both"'],
            showErrorMessage: true,
            errorTitle: "Invalid Skill",
            error: "Please select 'Batting', 'Bowling', or 'Both' from the dropdown list.",
          };
          if (r > 2) {
            skillsCell.alignment = { vertical: "middle", horizontal: "center" };
          }
        }

        // Date format (DD-MM-YYYY as text to prevent Excel auto-formatting errors)
        dateColIndices.forEach((colIdx) => {
          const dateCell = row.getCell(colIdx);
          dateCell.numFmt = "@"; // Plain text to keep exact DD-MM-YYYY format
        });
      }

      // 5. Generate and trigger download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, `${dataType}-bulk-upload-template.xlsx`);
      toast.success("Excel (.xlsx) Template downloaded successfully.", {
        position: "top-right",
        autoClose: 1500,
      });
    } catch (error) {
      console.error("Template generation error:", error);
      toast.error("Failed to generate Excel template.", {
        position: "top-right",
        autoClose: 2000,
      });
    }
  };

  // --------------------------------------------------
  // FILE SIZE
  // --------------------------------------------------
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";

    const sizes = ["Bytes", "KB", "MB", "GB"];

    const index = Math.floor(Math.log(bytes) / Math.log(1024));

    return (
      Math.round((bytes / Math.pow(1024, index)) * 100) / 100 +
      " " +
      sizes[index]
    );
  };

  // --------------------------------------------------
  // DATA TYPES
  // --------------------------------------------------
  const dataTypes = [
    {
      value: "player",
      label: "Players",
      icon: "fa fa-user",
      description: "Upload player records",
    },
    {
      value: "coach",
      label: "Coaches",
      icon: "fa fa-user-tie",
      description: "Upload coach records",
    },
    {
      value: "staff",
      label: "Staff",
      icon: "fa fa-users",
      description: "Upload staff records",
    },
  ];



  return (
    <>
      <PageTitle activeMenu="Bulk Data Upload" motherMenu="Academies" />

      <div>
        <div className="row">
          <div>
            <div>
              {/* <div className="card-header d-flex justify-content-between align-items-center">
                <div>
                  <h4 className="card-title mb-1">Bulk Data Upload</h4>

                  <small className="text-muted">
                    Upload coaches, players or staff using Excel / CSV
                  </small>
                </div>
              </div> */}

              <div>
                {/* ==================================================
                    TENANT INFORMATION
                ================================================== */}

                {/* <div
                  className="p-3 rounded-3 mb-4"
                  style={{
                    background: "#f8f9fa",
                    border: "1px solid #e9ecef",
                  }}
                > */}
                {/* <div className="row align-items-center">
                    <div className="col-md-6">
                      <small className="text-muted d-block">
                        Academy / Tenant
                      </small>

                      <h6 className="mb-0 mt-1">
                        {tenant?.name ||
                          tenant?.academyName ||
                          "Current Academy"}
                      </h6>
                    </div>

                    <div className="col-md-6 text-md-end mt-3 mt-md-0">
                      <small className="text-muted d-block">Tenant ID</small>

                      <span className="fw-semibold">{tenantId || "N/A"}</span>
                    </div>
                  </div> */}
                {/* </div> */}

                {/* ==================================================
                    DATA TYPE
                ================================================== */}

                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="fw-semibold mb-0">Select Data Type</h6>

                    {selectedFile && (
                      <small className="text-muted">
                        Remove the file to change type
                      </small>
                    )}
                  </div>

                  {/* DROPDOWN TYPE SELECTOR + DOWNLOAD TEMPLATE — same row */}
                  <div className="d-flex align-items-center gap-3 flex-wrap">
                    {/* Type selector */}
                    <Dropdown>
                      <Dropdown.Toggle
                        variant="outline-primary"
                        id="data-type-dropdown"
                        disabled={selectedFile !== null || isUploading}
                        className="d-flex align-items-center gap-2"
                        style={{ minWidth: 200 }}
                      >
                        {(() => {
                          const found = dataTypes.find((d) => d.value === dataType);
                          return found ? (
                            <>
                              <i className={found.icon} />
                              <span>{found.label}</span>
                            </>
                          ) : "Select Type";
                        })()}
                      </Dropdown.Toggle>

                      <Dropdown.Menu>
                        {dataTypes.map((item) => (
                          <Dropdown.Item
                            key={item.value}
                            active={dataType === item.value}
                            onClick={() => setDataType(item.value)}
                            className="d-flex align-items-center gap-2"
                          >
                            <i className={item.icon} style={{ width: 18 }} />
                            <div>
                              <div className="fw-semibold">{item.label}</div>
                              <small className="text-muted">{item.description}</small>
                            </div>
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>

                    {/* Download Excel Template */}
                    <Button
                      variant="outline-success"
                      id="download-template-btn"
                      className="d-flex align-items-center gap-2"
                      onClick={downloadExcelTemplate}
                      disabled={isUploading}
                    >
                      <i className="fa fa-file-excel" />
                      <span>Download Excel Template</span>
                    </Button>
                  </div>
                </div>

                {/* ==================================================
                    SELECTED TYPE
                ================================================== */}

                {selectedFile && (
                  <div className="mb-3">
                    <div
                      className="d-flex align-items-center gap-2 p-3 rounded-3"
                      style={{
                        background: "#e9f2ff",
                        border: "1px solid #b6d4fe",
                      }}
                    >
                      <i className="fa fa-info-circle text-primary" />

                      <div>
                        <small className="text-muted d-block">
                          Uploading as
                        </small>

                        <span className="fw-semibold text-primary">
                          {getDataTypeLabel(dataType)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* ==================================================
                    FILE UPLOAD
                ================================================== */}

                {!selectedFile ? (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className="text-center rounded-3 p-5"
                    style={{
                      cursor: "pointer",

                      border: isDragging
                        ? "2px dashed #0d6efd"
                        : "2px dashed #ced4da",

                      background: isDragging ? "#f0f6ff" : "#fafafa",

                      transition: "all 0.2s ease",
                    }}
                  >
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle mx-auto mb-3"
                      style={{
                        width: 65,
                        height: 65,
                        background: "#e9f2ff",
                        color: "#0d6efd",
                      }}
                    >
                      <i
                        className="fa fa-cloud-upload-alt"
                        style={{
                          fontSize: 28,
                        }}
                      />
                    </div>

                    <h5 className="fw-semibold mb-2">Drop your file here</h5>

                    <p className="text-muted mb-2">
                      or click to browse from your computer
                    </p>

                    <div className="text-muted small">
                      Upload {getDataTypeLabel(dataType).toLowerCase()} data
                    </div>

                    <div className="text-muted small mt-1">
                      Supported: .xlsx, .xls, .csv &nbsp; • &nbsp; Max 10 MB
                    </div>

                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleInputChange}
                      style={{
                        display: "none",
                      }}
                    />
                  </div>
                ) : (
                  /* ==================================================
                     SELECTED FILE
                  ================================================== */

                  <div
                    className="p-3 rounded-3"
                    style={{
                      border: "1px solid #dee2e6",
                      background: "#f8f9fa",
                    }}
                  >
                    <div className="d-flex align-items-center">
                      <div
                        className="d-flex align-items-center justify-content-center rounded-3 me-3"
                        style={{
                          width: 55,
                          height: 55,
                          background: "#e9f2ff",
                          color: "#0d6efd",
                        }}
                      >
                        <i
                          className="fa fa-file-excel"
                          style={{
                            fontSize: 24,
                          }}
                        />
                      </div>

                      <div className="flex-grow-1">
                        <div className="fw-semibold">{selectedFile.name}</div>

                        <small className="text-muted">
                          {formatFileSize(selectedFile.size)}
                        </small>
                      </div>

                      <Button
                        variant="link"
                        className="text-danger p-2"
                        onClick={removeFile}
                        disabled={isUploading}
                        title="Remove file"
                      >
                        <i className="fa fa-trash" />
                      </Button>
                    </div>

                    {/* Progress */}
                    {isUploading && (
                      <div className="mt-3">
                        <div className="d-flex justify-content-between mb-1">
                          <small className="text-muted">Uploading...</small>

                          <small className="text-muted">
                            {uploadProgress}%
                          </small>
                        </div>

                        <ProgressBar now={uploadProgress} animated />
                      </div>
                    )}
                  </div>
                )}


                {/* ==================================================
                    UPLOAD RESULT
                ================================================== */}

                {uploadResult && (
                  <div
                    className="mt-4 p-3 rounded-3"
                    style={{
                      border: uploadResult.success === false
                        ? "1px solid #f5c2c7"
                        : "1px solid #badbcc",
                      background: uploadResult.success === false
                        ? "#fff5f5"
                        : "#f0fff4",
                    }}
                  >
                    {/* Header */}
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <i
                        className={`fa ${uploadResult.success === false
                          ? "fa-times-circle text-danger"
                          : "fa-check-circle text-success"
                          } fs-5`}
                      />
                      <span className="fw-semibold">
                        {uploadResult.message}
                      </span>
                    </div>

                    {/* Stats Row */}
                    <div className="d-flex gap-3 flex-wrap mb-3">
                      {uploadResult.totalRows > 0 && (
                        <div
                          className="px-3 py-2 rounded-3 text-center"
                          style={{ background: "#e9ecef", minWidth: 90 }}
                        >
                          <div className="fw-bold">{uploadResult.totalRows}</div>
                          <small className="text-muted">Total Rows</small>
                        </div>
                      )}
                      {uploadResult.successCount > 0 && (
                        <div
                          className="px-3 py-2 rounded-3 text-center"
                          style={{ background: "#d1e7dd", minWidth: 90 }}
                        >
                          <div className="fw-bold text-success">{uploadResult.successCount}</div>
                          <small className="text-muted">Succeeded</small>
                        </div>
                      )}
                      {uploadResult.failedCount > 0 && (
                        <div
                          className="px-3 py-2 rounded-3 text-center"
                          style={{ background: "#f8d7da", minWidth: 90 }}
                        >
                          <div className="fw-bold text-danger">{uploadResult.failedCount}</div>
                          <small className="text-muted">Failed</small>
                        </div>
                      )}
                    </div>

                    {/* Error Details */}
                    {uploadResult.errors && (
                      <div
                        className="p-3 rounded-3"
                        style={{
                          background: "#fff",
                          border: "1px solid #f5c2c7",
                        }}
                      >
                        <div className="fw-semibold small mb-2 text-danger">
                          <i className="fa fa-exclamation-triangle me-1" />
                          Error Details
                        </div>

                        {/* Single error object */}
                        {!Array.isArray(uploadResult.errors) ? (
                          <div className="d-flex flex-column gap-1">
                            {uploadResult.errors.rowNumber && (
                              <div className="d-flex gap-2 small">
                                <span className="text-muted" style={{ minWidth: 80 }}>Row:</span>
                                <span className="fw-semibold">{uploadResult.errors.rowNumber}</span>
                              </div>
                            )}
                            {uploadResult.errors.field && (
                              <div className="d-flex gap-2 small">
                                <span className="text-muted" style={{ minWidth: 80 }}>Field:</span>
                                <span
                                  className="text-break"
                                  style={{ wordBreak: "break-all" }}
                                >
                                  {uploadResult.errors.field}
                                </span>
                              </div>
                            )}
                            {uploadResult.errors.message && (
                              <div className="d-flex gap-2 small">
                                <span className="text-muted" style={{ minWidth: 80 }}>Message:</span>
                                <span className="text-danger">{uploadResult.errors.message}</span>
                              </div>
                            )}
                          </div>
                        ) : (
                          /* Array of errors — table */
                          <div className="table-responsive">
                            <table className="table table-sm table-bordered mb-0">
                              <thead className="table-light">
                                <tr>
                                  <th>Row</th>
                                  <th>Field</th>
                                  <th>Message</th>
                                </tr>
                              </thead>
                              <tbody>
                                {uploadResult.errors.map((err, i) => (
                                  <tr key={i}>
                                    <td>{err.rowNumber ?? "-"}</td>
                                    <td className="text-break">{err.field ?? "-"}</td>
                                    <td className="text-danger">{err.message ?? "-"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Dismiss */}
                    <div className="text-end mt-2">
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 text-muted text-decoration-none"
                        onClick={() => setUploadResult(null)}
                      >
                        <i className="fa fa-times me-1" />Dismiss
                      </Button>
                    </div>
                  </div>
                )}

                {/* ==================================================
                    INFO
                ================================================== */}

                <div
                  className="mt-4 p-3 rounded-3"
                  style={{
                    background: "#f0f7ff",
                    border: "1px solid #cce3ff",
                  }}
                >
                  <div className="d-flex gap-2">
                    <i className="fa fa-info-circle text-primary mt-1" />

                    <div>
                      <div className="fw-semibold small text-primary mb-1">
                        Important Data Guidelines & Formatting
                      </div>

                      <ul className="mb-0 ps-3 small text-muted">
                        <li>
                          <strong>Excel Template:</strong> Click <strong>Download Excel Template</strong> to get the pre-formatted <code>.xlsx</code> file.
                        </li>
                        <li>
                          <strong>Gender:</strong> Select <code>Male</code>, <code>Female</code>, or <code>Others</code> from the Excel dropdown.
                        </li>
                        {dataType === "coach" && (
                          <li>
                            <strong>Skills:</strong> Select <code>Batting</code>, <code>Bowling</code>, or <code>Both</code> from the Excel dropdown.
                          </li>
                        )}
                        <li>
                          <strong>DOB & Dates:</strong> Must be in <code>DD-MM-YYYY</code> format (e.g. <code>10-05-2005</code>).
                        </li>
                        <li>
                          Make sure column headers are unchanged and required fields are filled.
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* ==================================================
                    ACTION BUTTONS
                ================================================== */}

                <div className="d-flex justify-content-end gap-2 mt-4">
                  <Button
                    variant="light"
                    className="px-4 rounded-pill"
                    onClick={() => navigate(-1)}
                    disabled={isUploading}
                  >
                    Cancel
                  </Button>

                  <Button
                    variant="primary"
                    className="px-4 rounded-pill"
                    onClick={handleUpload}
                    disabled={!selectedFile || isUploading || !tenantId}
                  >
                    {isUploading ? (
                      <>
                        <i className="fa fa-spinner fa-spin me-2" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <i className="fa fa-upload me-2" />
                        Upload {getDataTypeLabel(dataType)}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BulkDataUpload;
